import React, { createContext, useContext, useState, ReactNode } from 'react';
import { I18nProvider } from '@lingui/react';
import { i18n } from '@lingui/core';
import nl from '../loc/nl'
import fr from '../loc/fr'
import en from '../loc/en'
import de from '../loc/de'

// Define supported languages
const supportedLanguages = ['nl', 'en', 'fr', 'de'] as const;
type SupportedLanguage = (typeof supportedLanguages)[number];

interface LanguageContextProps {
    language: SupportedLanguage;
    setLanguage: (language: SupportedLanguage) => void;
}

const LanguageContext = createContext<LanguageContextProps | undefined>(undefined);

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

interface LanguageProviderProps {
    children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
    const [language, setLanguage] = useState<SupportedLanguage>('nl');

    // Load messages dynamically
    const loadMessages = async (locale: SupportedLanguage) => {
        let messages = nl;
        switch (locale) {
            case 'nl':
                messages = nl
                break;
            case 'fr':
                messages = fr
                break;
            case 'en':
                messages = en
                break;
            case 'de':
                messages = de
                break;
            default:
                messages = nl
                break;
        }
        i18n.load(locale, messages.messages); // Access the messages object
        i18n.activate(locale);
    };

    React.useEffect(() => {
        loadMessages(language);
    }, [language]);

    return (
        <LanguageContext.Provider value={{ language, setLanguage }}>
            <I18nProvider i18n={i18n}>
                {children}
            </I18nProvider>
        </LanguageContext.Provider>
    );
};
